import { buildOrderOption, buildUserOption } from '@witmetrics/unison-core';
import { searchByLabel } from '@witmetrics/utils/arrays';
import { isBlank } from '@witmetrics/utils/strings';
import { getRoundedDateTime } from '@witmetrics/utils/time';
import { API } from '@/api';

export const classes = {
  dialog: 'w-[98vw] max-w-[450px] min-w-[350px]',
  body: 'w-full max-h-[80vh] min-h-[100px] pt-2 pb-12 overflow-auto',
  input: 'w-full my-4',
};

type UserOption = { value: number; label: string };

export function getInitialDate(
  date: string | null,
  task?: { dueAt: string } | null
) {
  // Default to now if a value isn't already provided by the task or via app state
  let initialDate = new Date();
  if (task?.dueAt) initialDate = new Date(task.dueAt);
  else if (date) initialDate = new Date(date);
  return new Date(getRoundedDateTime(initialDate.toISOString()));
}

export function validateFields({
  dueAt,
  order,
  assignedTo,
  description,
}: {
  dueAt: string | null;
  order: any;
  assignedTo: any;
  description: string;
}) {
  let invalidFields = [];
  if (!dueAt) invalidFields.push('dueAt');
  if (isBlank(description)) invalidFields.push('description');
  if (!order) invalidFields.push('order');
  if (!assignedTo) invalidFields.push('assignedTo');
  return invalidFields;
}

export function buildTaskData({
  dueAt,
  userId,
  assignedTo,
  description,
}: {
  dueAt: string;
  userId?: number;
  assignedTo: UserOption;
  description: string;
}) {
  return {
    dueAt,
    userId,
    assignedTo: assignedTo.value,
    description: description.trim(),
  };
}

export async function fetchLabUsers(labId: number) {
  const users = await API.Labs.fetchLabUsers(labId, {
    filters: [],
    pagination: { page: 1, pageSize: 100 },
  });
  return users.results.map(({ user }) => buildUserOption(user));
}

export async function searchAssignedTo(
  searchQuery: string,
  labUsers: { value: number; label: string }[]
) {
  return searchByLabel(labUsers, searchQuery);
}

export async function fetchMatchingOrders(labId: number, search: string) {
  // NOTE: Ignoring pagination
  const { results } = await API.Labs.fetchLabOrders(labId, {
    search,
    filters: [],
    pagination: { page: 1, pageSize: 20 },
  });
  return results.map(buildOrderOption);
}

export async function createNewTask(
  orderId: number,
  data: ReturnType<typeof buildTaskData>
) {
  const task = await API.Tasks.createNewTask(data);
  const orderTask = await API.Orders.addTaskToOrder(orderId, task.id);
  return { task, orderTask };
}

export function updateTask(
  taskId: number,
  data: ReturnType<typeof buildTaskData>
) {
  return API.Tasks.updateTask(taskId, data);
}
